/**
 * Escolha de réplica do t-credentials, com contingência pegajosa.
 *
 * ── Por que existe ──────────────────────────────────────────────────────────
 * O t-credentials é caminho único para login, listagem de empresas e para tudo
 * que o app carrega no `/iniciar`. Com um host só, uma queda dele é o app inteiro
 * parado — e do lado de quem usa isso aparece como "Tempo de conexão esgotado",
 * sem nada que o aplicativo possa fazer a respeito.
 *
 * ── Por que a contingência é PEGAJOSA ───────────────────────────────────────
 * O ponto sutil não é escolher a segunda réplica: é NÃO voltar a tentar a
 * primeira a cada requisição. Um host fora do ar não recusa a conexão — ele
 * simplesmente não responde, e o cliente paga o timeout inteiro (10s) antes de
 * desistir. Sem memória, cada chamada durante a queda custaria 10 segundos ANTES
 * de começar a que vai funcionar, e o app ficaria inutilizável mesmo com uma
 * réplica saudável ao lado.
 *
 * Por isso a primeira falha muda a preferência e ela FICA — por uma janela. Ao
 * fim dela a primária é tentada de novo, uma vez; se responder, volta a ser a
 * preferida. É o suficiente para o retorno ser automático sem transformar cada
 * requisição numa sonda.
 */

/**
 * Quanto tempo a preferência por uma réplica de contingência dura antes de a
 * primária ser tentada de novo.
 *
 * Cinco minutos: curto o bastante para o retorno ser rápido depois de um deploy
 * ou de uma queda breve, e longo o bastante para não pagar o timeout da primária
 * com frequência enquanto ela está fora.
 */
const JANELA_CONTINGENCIA_MS = 5 * 60 * 1000;

export type SeletorReplica = {
  /** Hosts a tentar, na ordem — o preferido primeiro. */
  ordem: () => string[];
  /** A base não respondeu (rede, timeout ou gateway fora). */
  registrarFalha: (base: string) => void;
  /** A base respondeu — inclusive com erro de negócio, que é resposta. */
  registrarSucesso: (base: string) => void;
  /** Está usando algo que não é a primária. */
  emContingencia: () => boolean;
};

export function criarSeletorReplica(
  bases: string[],
  rotulo = "t-credentials",
): SeletorReplica {
  /** Índice do host preferido AGORA. 0 = primária. */
  let ativo = 0;
  /** Quando voltar a preferir a primária (epoch ms). 0 = já é a preferida. */
  let expiraEm = 0;

  const normalizar = () => {
    if (ativo !== 0 && expiraEm > 0 && Date.now() >= expiraEm) {
      console.log(`[${rotulo}] janela de contingência venceu, tentando a primária.`);
      ativo = 0;
      expiraEm = 0;
    }
  };

  return {
    ordem() {
      normalizar();
      // Todas as bases, começando pela preferida: se a preferida falhar, a
      // próxima da lista é tentada na MESMA requisição — quem chamou não vê a
      // troca acontecer, só a resposta.
      return [...bases.slice(ativo), ...bases.slice(0, ativo)];
    },

    registrarFalha(base) {
      const i = bases.indexOf(base);
      // Só a base PREFERIDA move a preferência. Sem esta guarda, a falha da
      // segunda réplica (tentada logo depois da primeira na mesma requisição)
      // avançaria o ponteiro de novo e daria a volta até a primária — que acabou
      // de falhar —, fazendo o ciclo recomeçar do host quebrado.
      if (i !== ativo || bases.length < 2) return;

      ativo = (ativo + 1) % bases.length;
      expiraEm = Date.now() + JANELA_CONTINGENCIA_MS;
      console.warn(
        `[${rotulo}] ${base} não respondeu — contingência para ${bases[ativo]} ` +
          `pelos próximos ${Math.round(JANELA_CONTINGENCIA_MS / 60000)} min.`,
      );
    },

    registrarSucesso(base) {
      const i = bases.indexOf(base);
      if (i < 0 || i === ativo) return;
      // Respondeu uma base que não era a preferida (o caso normal: a janela
      // venceu, a primária foi tentada e voltou). Ela passa a ser a preferida.
      ativo = i;
      expiraEm = i === 0 ? 0 : Date.now() + JANELA_CONTINGENCIA_MS;
      if (i === 0) console.log(`[${rotulo}] primária respondeu, contingência encerrada.`);
    },

    emContingencia: () => {
      normalizar();
      return ativo !== 0;
    },
  };
}

/**
 * Troca só a ORIGEM de uma URL, preservando caminho e query.
 *
 * As chamadas espalhadas pelo app passam `baseUrl` já montada (às vezes com
 * caminho, como o `/api/start/iniciar`). Reescrever a origem preserva tudo isso
 * sem exigir que cada ponto de chamada saiba que existe réplica.
 *
 * Devolve a URL original se qualquer coisa não fizer sentido: uma troca de host
 * malfeita mandaria a requisição para um lugar errado, e o certo é seguir para
 * onde quem chamou pediu.
 */
export function trocarOrigem(url: string, novaOrigem: string): string {
  try {
    const alvo = new URL(url);
    const nova = new URL(novaOrigem);
    alvo.protocol = nova.protocol;
    alvo.host = nova.host;
    return alvo.toString();
  } catch {
    return url;
  }
}

/** As duas URLs apontam para o mesmo host? */
export function mesmaOrigem(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  try {
    return new URL(a).host === new URL(b).host;
  } catch {
    return false;
  }
}
